<template>
    <div class="economic-report mt-3">
        <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0">{{ $t("economic_report") }}</h5>
                <div class="d-flex align-items-center">
                    <label class="me-2">{{ $t("year") }}:</label>
                    <select class="form-select form-select-sm" style="width: auto;" v-model="selectedYear" @change="loadReport">
                        <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
                    </select>
                </div>
            </div>
            <div class="card-body">
                <div v-if="loading" class="text-center py-4">
                    <div class="spinner-border" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>

                <template v-else-if="report && report.totals">
                    <!-- Zusammenfassung Produktion -->
                    <div class="row g-3 mb-4">
                        <div class="col-md-3">
                            <div class="card shadow-sm border-0 bg-light h-100">
                                <div class="card-body text-center py-3">
                                    <h6 class="card-subtitle mb-2 text-muted small">{{ $t("actual_production") }}</h6>
                                    <h4 class="card-title mb-0">{{ formatNumber(report.totals.actual_kwh) }} kWh</h4>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-3">
                            <div class="card shadow-sm border-0 bg-light h-100">
                                <div class="card-body text-center py-3">
                                    <h6 class="card-subtitle mb-2 text-muted small">{{ $t("target_production") }}</h6>
                                    <h4 class="card-title mb-0">{{ formatNumber(report.totals.target_kwh) }} kWh</h4>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-3">
                            <div class="card shadow-sm border-0 h-100" :class="report.totals.difference_kwh >= 0 ? 'bg-success-subtle' : 'bg-danger-subtle'">
                                <div class="card-body text-center py-3">
                                    <h6 class="card-subtitle mb-2 text-muted small">{{ $t("difference") }}</h6>
                                    <h4 class="card-title mb-0">
                                        <span :class="report.totals.difference_kwh >= 0 ? 'text-success' : 'text-danger'">
                                            {{ report.totals.difference_kwh >= 0 ? '+' : '' }}{{ formatNumber(report.totals.difference_kwh) }} kWh
                                        </span>
                                    </h4>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-3">
                            <div class="card shadow-sm border-0 h-100" :class="report.totals.achievement_percent >= 100 ? 'bg-success-subtle' : 'bg-warning-subtle'">
                                <div class="card-body text-center py-3">
                                    <h6 class="card-subtitle mb-2 text-muted small">{{ $t("achievement_percent") }}</h6>
                                    <h4 class="card-title mb-0">{{ report.totals.achievement_percent.toFixed(1) }}%</h4>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Zusammenfassung Erlös -->
                    <div class="row g-3 mb-4">
                        <div class="col-md-4">
                            <div class="card shadow-sm border-0 bg-light h-100">
                                <div class="card-body text-center py-3">
                                    <h6 class="card-subtitle mb-2 text-muted small">{{ $t("revenue_actual") }}</h6>
                                    <h4 class="card-title mb-0">{{ formatCurrency(report.totals.revenue_actual) }}</h4>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="card shadow-sm border-0 bg-light h-100">
                                <div class="card-body text-center py-3">
                                    <h6 class="card-subtitle mb-2 text-muted small">{{ $t("revenue_target") }}</h6>
                                    <h4 class="card-title mb-0">{{ formatCurrency(report.totals.revenue_target) }}</h4>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="card shadow-sm border-0 h-100" :class="report.totals.revenue_difference >= 0 ? 'bg-success-subtle' : 'bg-danger-subtle'">
                                <div class="card-body text-center py-3">
                                    <h6 class="card-subtitle mb-2 text-muted small">{{ $t("revenue_difference") }}</h6>
                                    <h4 class="card-title mb-0">
                                        <span :class="report.totals.revenue_difference >= 0 ? 'text-success' : 'text-danger'">
                                            {{ report.totals.revenue_difference >= 0 ? '+' : '' }}{{ formatCurrency(report.totals.revenue_difference) }}
                                        </span>
                                    </h4>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Monatliche Tabelle -->
                    <div class="table-responsive">
                        <table class="table table-striped table-hover table-bordered">
                            <thead class="table-light">
                                <tr>
                                    <th>{{ $t("month") }}</th>
                                    <th class="text-end">{{ $t("target_kwh") }}</th>
                                    <th class="text-end">{{ $t("actual_kwh") }}</th>
                                    <th class="text-end">{{ $t("difference") }}</th>
                                    <th class="text-end">{{ $t("achievement") }}</th>
                                    <th class="text-end">{{ $t("price_per_kwh_short") }}</th>
                                    <th class="text-end">{{ $t("revenue_target") }}</th>
                                    <th class="text-end">{{ $t("revenue_actual") }}</th>
                                    <th class="text-end">{{ $t("revenue_difference") }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="month in report.months" :key="month.month">
                                    <td>{{ month.month_name }}</td>
                                    <td class="text-end">{{ formatNumber(month.target_kwh) }}</td>
                                    <td class="text-end">{{ formatNumber(month.actual_kwh) }}</td>
                                    <td class="text-end" :class="month.difference_kwh >= 0 ? 'text-success' : 'text-danger'">
                                        {{ month.difference_kwh >= 0 ? '+' : '' }}{{ formatNumber(month.difference_kwh) }}
                                    </td>
                                    <td class="text-end">
                                        <span class="badge" :class="getAchievementBadgeClass(month.achievement_percent)">
                                            {{ month.achievement_percent.toFixed(1) }}%
                                        </span>
                                    </td>
                                    <td class="text-end">{{ formatPrice(month.electricity_price) }}</td>
                                    <td class="text-end">{{ formatCurrency(month.revenue_target) }}</td>
                                    <td class="text-end">{{ formatCurrency(month.revenue_actual) }}</td>
                                    <td class="text-end" :class="month.revenue_difference >= 0 ? 'text-success' : 'text-danger'">
                                        {{ month.revenue_difference >= 0 ? '+' : '' }}{{ formatCurrency(month.revenue_difference) }}
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot class="table-light fw-bold">
                                <tr>
                                    <td>{{ $t("total") }}</td>
                                    <td class="text-end">{{ formatNumber(report.totals.target_kwh) }}</td>
                                    <td class="text-end">{{ formatNumber(report.totals.actual_kwh) }}</td>
                                    <td class="text-end" :class="report.totals.difference_kwh >= 0 ? 'text-success' : 'text-danger'">
                                        {{ report.totals.difference_kwh >= 0 ? '+' : '' }}{{ formatNumber(report.totals.difference_kwh) }}
                                    </td>
                                    <td class="text-end">
                                        <span class="badge" :class="getAchievementBadgeClass(report.totals.achievement_percent)">
                                            {{ report.totals.achievement_percent.toFixed(1) }}%
                                        </span>
                                    </td>
                                    <td class="text-end">-</td>
                                    <td class="text-end">{{ formatCurrency(report.totals.revenue_target) }}</td>
                                    <td class="text-end">{{ formatCurrency(report.totals.revenue_actual) }}</td>
                                    <td class="text-end" :class="report.totals.revenue_difference >= 0 ? 'text-success' : 'text-danger'">
                                        {{ report.totals.revenue_difference >= 0 ? '+' : '' }}{{ formatCurrency(report.totals.revenue_difference) }}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <!-- Info -->
                    <div class="row mt-3">
                        <div class="col-12">
                            <small class="text-muted">
                                {{ $t("production_field") }}: {{ report.production_field?.name || '-' }}
                                ({{ report.production_field?.component || '-' }})
                            </small>
                        </div>
                    </div>

                    <!-- Strompreis-Historie -->
                    <div class="row mt-2" v-if="report.electricity_prices && report.electricity_prices.length > 0">
                        <div class="col-12">
                            <small class="text-muted">
                                {{ $t("electricity_prices") }}:
                                <span v-for="(price, index) in report.electricity_prices" :key="index">
                                    {{ formatPrice(price.price_per_kwh) }} EUR/kWh
                                    ({{ formatDate(price.valid_from) }} - {{ price.valid_to ? formatDate(price.valid_to) : $t("unlimited") }})
                                    <span v-if="index < report.electricity_prices.length - 1">, </span>
                                </span>
                            </small>
                        </div>
                    </div>
                </template>

                <div v-else class="text-center py-4 text-muted">
                    {{ $t("no_data_available") }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import apiClient from '@/services/apiClient'

export default {
    name: 'EconomicReport',
    props: {
        plant: {
            type: Object,
            required: true
        },
        economicConfig: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            report: null,
            loading: false,
            selectedYear: new Date().getFullYear()
        }
    },
    computed: {
        availableYears() {
            const currentYear = new Date().getFullYear()
            const years = []
            for (let i = currentYear; i >= currentYear - 5; i--) {
                years.push(i)
            }
            return years
        }
    },
    watch: {
        economicConfig: {
            handler(newVal) {
                if (newVal && newVal.id) {
                    this.loadReport()
                }
            },
            immediate: true
        }
    },
    methods: {
        async loadReport() {
            if (!this.economicConfig || !this.economicConfig.id) return

            this.loading = true
            try {
                const response = await apiClient.getEconomicReport(this.economicConfig.id, this.selectedYear)
                this.report = response.data
            } catch (error) {
                console.error('Error loading economic report:', error)
                this.report = null
            } finally {
                this.loading = false
            }
        },
        formatNumber(value) {
            return new Intl.NumberFormat('de-DE', {
                maximumFractionDigits: 0
            }).format(value)
        },
        formatCurrency(value) {
            return new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: 'EUR'
            }).format(value)
        },
        getAchievementBadgeClass(percent) {
            if (percent >= 100) return 'bg-success'
            if (percent >= 90) return 'bg-warning text-dark'
            return 'bg-danger'
        },
        formatPrice(value) {
            if (value === undefined || value === null) return '-'
            return new Intl.NumberFormat('de-DE', {
                minimumFractionDigits: 4,
                maximumFractionDigits: 4
            }).format(value)
        },
        formatDate(dateStr) {
            if (!dateStr) return '-'
            const date = new Date(dateStr)
            return date.toLocaleDateString('de-DE')
        }
    }
}
</script>

<style scoped>
.bg-success-subtle {
    background-color: rgba(25, 135, 84, 0.1) !important;
}
.bg-danger-subtle {
    background-color: rgba(220, 53, 69, 0.1) !important;
}
.bg-warning-subtle {
    background-color: rgba(255, 193, 7, 0.1) !important;
}
</style>
